import { Button } from "@/components/ui/button";
import { Save, Clock, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EditorActionsProps {
  onSaveDraft: () => void;
  onSchedule: () => void;
  onPublish: () => void;
  contentType?: "draft" | "scheduled";
  content: string;
  images?: string[];
  video?: string | null;
}

export const EditorActions = ({
  onSaveDraft,
  onSchedule,
  onPublish,
  contentType = "draft",
  content,
  images = [],
  video
}: EditorActionsProps) => {
  const handlePublish = async () => {
    try {
      // First call onPublish to get the formatted content with mentions
      const formattedContent = onPublish();

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to publish");
        return;
      }

      // Get video size for the API
      let videoData;
      if (video) {
        const response = await fetch(video);
        if (!response.ok) {
          throw new Error('Failed to fetch video data');
        }
        const blob = await response.blob();
        videoData = [{
          url: video,
          size: blob.size
        }];
      }

      const { data, error } = await supabase.functions.invoke('post-to-linkedin-new', {
        body: { 
          content: formattedContent,
          images,
          videos: videoData
        }
      });

      if (error) {
        console.error('LinkedIn post error:', error);
        let errorMessage = error.message || "Failed to post to LinkedIn";
        
        if (error.message?.startsWith('{')) {
          try {
            const errorBody = JSON.parse(error.message);
            if (errorBody.error) {
              errorMessage = errorBody.error;
            }
          } catch (e) {
            console.error('Error parsing error response:', e);
          }
        }
        
        toast.error(errorMessage);
        return;
      }
      
      toast.success("Successfully posted to LinkedIn!");
    } catch (error) {
      console.error('Error posting to LinkedIn:', error);
      toast.error("Failed to post to LinkedIn. Please make sure you're connected to LinkedIn in Settings.");
    }
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {contentType === "draft" ? (
          <Button onClick={onSaveDraft} variant="outline">
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
        ) : (
          <Button onClick={onSaveDraft} variant="outline">
            <Save className="w-4 h-4 mr-2" />
            Save Schedule
          </Button>
        )}
        {contentType === "draft" && (
          <>
            <Button onClick={onSchedule}>
              <Clock className="w-4 h-4 mr-2" />
              Schedule
            </Button>
            <Button
              variant="secondary"
              onClick={handlePublish}
            >
              <Send className="w-4 h-4 mr-2" />
              Publish
            </Button>
          </>
        )}
      </div>
    </div>
  );
};